import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FlaskConical, Plus, Users, RotateCcw, Trash2, ExternalLink, Loader2, Search, X, Shuffle, Monitor, Radio } from "lucide-react";
import TournamentCreateForm from "@/components/TournamentCreateForm";
import TdTrainingScript from "@/components/TdTrainingScript";

const lbl = "block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5";
const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red text-sm";

function PlayerPicker({ tourney, onDone }) {
  const [ev, setEv] = useState(tourney.event_types?.[0] || "");
  const [ab, setAb] = useState(tourney.age_brackets?.[0] || "");
  const divOpts = (tourney.levels || []).filter((l) => l !== "All");
  const [division, setDivision] = useState(divOpts[0] || "Intermediate");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState([]);
  const [teams, setTeams] = useState(8);
  const [busy, setBusy] = useState(false);
  const isDoubles = ev.toLowerCase().includes("doubles");

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const id = setTimeout(() => {
      api.get(`/members/search?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => setResults(data || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const add = (p) => {
    if (!picked.find((x) => x.id === p.id)) setPicked((prev) => [...prev, p]);
    setQ(""); setResults([]);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/demo-tournaments/${tourney.id}/entries`,
        { user_ids: picked.map((p) => p.id), event_type: ev, age_bracket: ab || null, division });
      toast.success(`${data.added} entr${data.added === 1 ? "y" : "ies"} added to ${ev}`);
      setPicked([]);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const autofill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/demo-tournaments/${tourney.id}/autofill`,
        { event_type: ev, age_bracket: ab || null, division, teams: Number(teams) || 8 });
      toast.success(`Auto-filled ${data.added} entr${data.added === 1 ? "y" : "ies"} with ${data.players} random members`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border border-cyan-500/25 bg-black/40 p-4 space-y-4" data-testid="demo-player-picker">
      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className={lbl}>Event</label>
          <select data-testid="demo-pick-event" value={ev} onChange={(e) => setEv(e.target.value)} className={input}>
            {(tourney.event_types || []).map((e2) => <option key={e2}>{e2}</option>)}
          </select></div>
        {(tourney.age_brackets || []).length > 0 && (
          <div><label className={lbl}>Age Bracket</label>
            <select data-testid="demo-pick-age" value={ab} onChange={(e) => setAb(e.target.value)} className={input}>
              {tourney.age_brackets.map((a) => <option key={a}>{a}</option>)}
            </select></div>
        )}
        <div><label className={lbl}>Division</label>
          <select data-testid="demo-pick-division" value={division} onChange={(e) => setDivision(e.target.value)} className={input}>
            {(divOpts.length ? divOpts : ["Beginner", "Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Contender", "Future Pro", "Pro"]).map((d) => <option key={d}>{d}</option>)}
          </select></div>
      </div>

      <div>
        <label className={lbl}>Add Registered Players {isDoubles && <span className="text-cyan-400 normal-case tracking-normal">(pick order pairs them — every 2 picks = 1 team)</span>}</label>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input data-testid="demo-player-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search any registered member…" className={`${input} pl-9`} />
          {results.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-20 bg-pb-surface border border-pb-border shadow-2xl max-h-52 overflow-y-auto">
              {results.map((r) => (
                <button key={r.id} data-testid={`demo-player-result-${r.id}`} onClick={() => add(r)}
                  className="w-full text-left px-3 py-2 text-sm text-zinc-300 hover:bg-pb-elevated hover:text-white transition-colors">
                  {r.name} <span className="text-[10px] text-zinc-600">{r.email || ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {picked.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="demo-picked-list">
            {picked.map((p, i) => (
              <span key={p.id} className="inline-flex items-center gap-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-xs px-2 py-1">
                {isDoubles && <b className="text-[9px] text-cyan-400">T{Math.floor(i / 2) + 1}</b>} {p.name}
                <button data-testid={`demo-picked-remove-${p.id}`} onClick={() => setPicked((prev) => prev.filter((x) => x.id !== p.id))}
                  className="text-cyan-400 hover:text-white"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="demo-add-selected-btn" onClick={submit} disabled={busy || !picked.length}
          className="inline-flex items-center gap-2 bg-cyan-500 text-black font-bold uppercase text-xs px-4 py-2 hover:bg-cyan-400 disabled:opacity-40 transition-colors">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
          Add {picked.length ? (isDoubles ? `${Math.ceil(picked.length / 2)} team${Math.ceil(picked.length / 2) === 1 ? "" : "s"}` : `${picked.length} player${picked.length === 1 ? "" : "s"}`) : "selected"}
        </button>
        <span className="text-[10px] uppercase tracking-widest text-zinc-600">or</span>
        <div className="inline-flex items-center gap-2">
          <input data-testid="demo-autofill-count" type="number" min="2" max="32" value={teams}
            onChange={(e) => setTeams(e.target.value)} className={`${input} w-16 text-center`} />
          <button data-testid="demo-autofill-btn" onClick={autofill} disabled={busy}
            className="inline-flex items-center gap-2 border border-cyan-500/40 text-cyan-300 font-bold uppercase text-xs px-4 py-2 hover:bg-cyan-500/10 disabled:opacity-40 transition-colors">
            <Shuffle className="w-3.5 h-3.5" /> Auto-fill {isDoubles ? "teams" : "players"} at random
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DemoTournamentLab() {
  const [list, setList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [openPicker, setOpenPicker] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/admin/demo-tournaments").then(({ data }) => setList(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = async (t) => {
    if (!window.confirm(`Reset "${t.name}"? All brackets and scores are wiped — entries stay so you can re-run the demo.`)) return;
    try { await api.post(`/admin/demo-tournaments/${t.id}/reset`); toast.success("Demo reset — brackets cleared, roster kept"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleLiveFlow = async (t) => {
    try {
      const { data } = await api.post(`/admin/demo-tournaments/${t.id}/live-flow`, { enabled: !t.live_flow });
      toast.success(data.live_flow
        ? "Live Player Flow ON — participants who are signed in now get the real in-app notifications (no emails, no SMS, still zero real-world impact)"
        : "Live Player Flow OFF — silent sandbox");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete demo "${t.name}" completely? Entries, brackets and scores are all removed.`)) return;
    try { await api.delete(`/tournaments/${t.id}`); toast.success("Demo tournament deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="demo-tournament-lab">
      <div className="border border-cyan-500/30 bg-cyan-500/5 p-4 text-xs text-zinc-400 leading-relaxed">
        <div className="flex items-center gap-2 text-cyan-300 font-bold uppercase tracking-wider mb-1.5">
          <FlaskConical className="w-4 h-4" /> Sandbox — zero real-world impact
        </div>
        Run the EXACT tournament flow (create → roster → draws → courts → scores → advance → champions) as a private rehearsal
        or an investor walkthrough. Demo events run under the SAME laws as official play — registration windows, waitlists,
        substitutions, score approval, division separation, medal courts, bronze matches, champions. They stay invisible to members:
        no PSI/PBR changes, no community posts, no champion bounties, no tournament stats, no Champions Wall, no revenue records,
        and no entry fees. Flip <b className="text-lime-300">Live Player Flow</b> on any demo to fire the real in-app notifications
        (match assignments, score approvals, gold thank-you letter) to its participants — so a TD signed in on their device
        experiences exactly what a player sees. Emails and SMS never send from the Lab.
      </div>

      <TdTrainingScript demos={list} />

      <button data-testid="demo-create-toggle" onClick={() => setShowCreate((s) => !s)}
        className="inline-flex items-center gap-2 bg-cyan-500 text-black font-bold uppercase text-xs px-4 py-2.5 hover:bg-cyan-400 transition-colors">
        <Plus className="w-4 h-4" /> {showCreate ? "Hide form" : "Create Demo Tournament"}
      </button>
      {showCreate && (
        <div className="border border-pb-border bg-pb-surface p-4">
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-400 mb-3">Same form the Tournament Director fills out — every style and option</div>
          <TournamentCreateForm endpoint="/admin/demo-tournaments" onCreated={() => { setShowCreate(false); load(); }} />
        </div>
      )}

      {list.length === 0 && !showCreate && (
        <div className="text-sm text-zinc-600" data-testid="demo-empty">No demo tournaments yet — create one to start rehearsing.</div>
      )}

      <div className="space-y-3">
        {list.map((t) => (
          <div key={t.id} className="border border-pb-border bg-pb-surface p-4" data-testid={`demo-tournament-${t.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display font-black uppercase text-white">{t.name}</span>
                  <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-500 text-black px-1.5 py-0.5">Demo</span>
                  <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border ${
                    t.status === "complete" ? "border-zinc-600 text-zinc-500" : t.status === "in_progress" ? "border-cyan-400 text-cyan-300" : "border-lime-400 text-lime-300"}`}>
                    {t.status === "in_progress" ? "Live" : t.status === "complete" ? "Final" : "Open"}
                  </span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {t.format?.replace("_", " ")} · {(t.event_types || []).join(", ")} · {t.date} · {t.entry_count} entries · {t.match_count} matches
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid={`demo-liveflow-btn-${t.id}`} onClick={() => toggleLiveFlow(t)}
                  title="When ON, participants who are signed in receive the real in-app notifications + gold letter. No emails/SMS ever."
                  className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase px-3 py-1.5 border transition-colors ${
                    t.live_flow ? "border-lime-400 text-lime-300 bg-lime-400/10" : "border-pb-border text-zinc-500 hover:border-lime-400 hover:text-lime-300"}`}>
                  <Radio className={`w-3.5 h-3.5 ${t.live_flow ? "animate-pulse" : ""}`} />
                  {t.live_flow ? "Live Player Flow ON" : "Live Player Flow"}
                </button>
                <button data-testid={`demo-players-btn-${t.id}`} onClick={() => setOpenPicker(openPicker === t.id ? null : t.id)}
                  className="inline-flex items-center gap-1.5 border border-cyan-500/40 text-cyan-300 text-[11px] font-bold uppercase px-3 py-1.5 hover:bg-cyan-500/10 transition-colors">
                  <Users className="w-3.5 h-3.5" /> Players
                </button>
                <button data-testid={`demo-console-btn-${t.id}`} onClick={() => navigate(`/tournaments/${t.id}`)}
                  className="inline-flex items-center gap-1.5 bg-lime-400 text-black text-[11px] font-bold uppercase px-3 py-1.5 hover:bg-lime-300 transition-colors">
                  <ExternalLink className="w-3.5 h-3.5" /> Run It — TD Console
                </button>
                <a data-testid={`demo-courtboard-btn-${t.id}`} href={`/court-board/${t.id}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[11px] font-bold uppercase px-3 py-1.5 hover:text-white transition-colors">
                  <Monitor className="w-3.5 h-3.5" /> Court Board
                </a>
                <button data-testid={`demo-reset-btn-${t.id}`} onClick={() => reset(t)} title="Wipe brackets + scores, keep the roster"
                  className="inline-flex items-center gap-1.5 border border-amber-500/40 text-amber-300 text-[11px] font-bold uppercase px-3 py-1.5 hover:bg-amber-500/10 transition-colors">
                  <RotateCcw className="w-3.5 h-3.5" /> Reset
                </button>
                <button data-testid={`demo-delete-btn-${t.id}`} onClick={() => remove(t)}
                  className="inline-flex items-center gap-1.5 border border-red-500/40 text-red-400 text-[11px] font-bold uppercase px-3 py-1.5 hover:bg-red-500/10 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
            {openPicker === t.id && <PlayerPicker tourney={t} onDone={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}
